import { Card, Container, type ContainerProps, Modal, type ModalProps, Stack, type StackProps } from '@mui/material';
import type React from 'react';
import type { ReactNode } from 'react';
import ModalV2Actions, { type ModalV2ActionsProps } from '../modal-actions';
import ModalV2Header from '../modal-header';

type ModalV2Props = {
    open: boolean;
    onClose?: () => void;
    goBack?: () => void;
    header?: ReactNode;
    /** Not HTML `title`; supports icons / JSX (see ModalV2Header). */
    title?: ReactNode;
    children?: ReactNode;
    actions?: ReactNode;
    maxWidth?: ContainerProps['maxWidth'];
    containerProps?: ContainerProps;
    bodyProps?: StackProps;
    actionsProps?: ModalV2ActionsProps;
} & Omit<ModalProps, 'title'>;

const ModalV2: React.FC<ModalV2Props> = ({
    open,
    onClose,
    goBack,
    header,
    title,
    children,
    actions,
    maxWidth = 'sm',
    actionsProps,
    containerProps,
    bodyProps,
    ...modalProps
}) => {
    return (
        <Modal open={open} onClose={onClose} {...modalProps}>
            <Container
                maxWidth={maxWidth}
                {...containerProps}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',

                    ...containerProps?.sx,
                }}
            >
                <Card
                    sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 0,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        // `vh` is the large viewport on mobile browsers (URL bar retracted), so a
                        // vh-capped centered card overflows the visible area at both edges — and the
                        // overflow is unreachable, since the container is absolutely centered.
                        maxHeight: '90vh',
                        '@supports (max-height: 1dvh)': {
                            maxHeight: '90dvh',
                        },
                    }}
                >
                    {header ? (
                        header
                    ) : (
                        <ModalV2Header onClose={onClose} goBack={goBack}>
                            {title}
                        </ModalV2Header>
                    )}
                    <Stack
                        spacing={2}
                        alignItems='center'
                        textAlign='center'
                        p={{ xs: 1, sm: 3 }}
                        {...bodyProps}
                        sx={{
                            overflow: 'auto',
                            ...bodyProps?.sx,
                        }}
                    >
                        {children}
                    </Stack>
                    {actions && (
                        <ModalV2Actions {...actionsProps} sx={{ mt: 'auto', ...actionsProps?.sx }}>
                            {actions}
                        </ModalV2Actions>
                    )}
                </Card>
            </Container>
        </Modal>
    );
};

export default ModalV2;
